import { useState } from "react";
import { Box, IconButton, Collapse } from "@mui/material";
import { ChevronLeft, ChevronRight, Close, ExpandMore } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import CustomTypography from "../../../Components/Typography";
import {
  productDetailPanelStyle,
  discountTagStyle,
  carouselArrowStyle,
} from "../styles/styles";
import { getCurrencySymbol } from "../../../Utils/CountriesData/getCountriesData";

const ProductDetailPanel = ({ product, onClose }) => {
  const { t, i18n } = useTranslation("supplier-products-info");
  const isHebrew = i18n.language === "he";
  const [imageIndex, setImageIndex] = useState(0);
  // All sections start collapsed when a product is opened.
  const [openSections, setOpenSections] = useState({
    description: false,
    terms: false,
  });

  if (!product) return null;

  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const images = product.images?.length
    ? product.images
    : [product.main_image_url].filter(Boolean);
  const hasMultipleImages = images.length > 1;
  const currencySymbol = getCurrencySymbol(product.currency);

  const discountRate = product.discount_rate
    ? Math.round(product.discount_rate)
    : 0;
  const discountedPrice =
    product.discounted_price != null ? product.discounted_price : product.price;

  const goPrev = () =>
    setImageIndex((i) => (i - 1 + images.length) % images.length);
  const goNext = () => setImageIndex((i) => (i + 1) % images.length);

  // Arrows mirror with reading direction so "next" always advances visually.
  const PrevIcon = isHebrew ? ChevronRight : ChevronLeft;
  const NextIcon = isHebrew ? ChevronLeft : ChevronRight;

  const renderSection = (key, title, body) => (
    <Box>
      <Box
        onClick={() => toggleSection(key)}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ cursor: "pointer", py: "12px" }}
      >
        <CustomTypography
          props={{ color: "#2F2B3D", fontSize: "15px", fontWeight: 500 }}
        >
          {title}
        </CustomTypography>
        <ExpandMore
          sx={{
            fontSize: "20px",
            color: "#2F2B3D",
            transform: openSections[key] ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.3s",
          }}
        />
      </Box>
      <Collapse in={openSections[key]}>
        {/* Fixed-height body with its own scroll so a long description doesn't
            push the rest of the panel down. */}
        <Box
          className="custom-scrollbar"
          sx={{
            maxHeight: "150px",
            overflowY: "auto",
            pb: "12px",
            pr: "4px",
          }}
        >
          <CustomTypography
            props={{
              color: "#2F2B3DB2",
              fontSize: "13px",
              fontWeight: 400,
              lineHeight: "20px",
              whiteSpace: "pre-line",
            }}
          >
            {body || "—"}
          </CustomTypography>
        </Box>
      </Collapse>
    </Box>
  );

  return (
    <Box sx={productDetailPanelStyle}>
      {/* On desktop the modal header's close is visible just above, so the panel's
          own close is mobile-only (the panel covers the header on mobile). */}
      <Box
        display="flex"
        justifyContent="flex-start"
        sx={{ p: "8px", display: { xs: "flex", md: "none" } }}
      >
        <IconButton onClick={onClose} aria-label="close-product-detail">
          <Close sx={{ fontSize: "20px", color: "#2F2B3D" }} />
        </IconButton>
      </Box>

      <Box sx={{ position: "relative", px: "16px", pt: { xs: 0, md: "16px" } }}>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "200px",
            borderRadius: "8px",
            overflow: "hidden",
            backgroundColor: "#F4F4F4",
          }}
        >
          {images[imageIndex] ? (
            <img
              src={images[imageIndex]}
              alt={product.name}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : null}

          {hasMultipleImages && (
            <>
              <IconButton
                onClick={goPrev}
                aria-label="previous-image"
                sx={carouselArrowStyle("start")}
              >
                <PrevIcon sx={{ fontSize: "20px", color: "#fff" }} />
              </IconButton>
              <IconButton
                onClick={goNext}
                aria-label="next-image"
                sx={carouselArrowStyle("end")}
              >
                <NextIcon sx={{ fontSize: "20px", color: "#fff" }} />
              </IconButton>
            </>
          )}
        </Box>
      </Box>

      <Box sx={{ px: "16px", pt: "24px", pb: "16px" }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap="12px"
          mb="12px"
        >
          {discountRate > 0 && (
            <Box sx={discountTagStyle}>
              <Box component="span" dir="ltr">
                &minus;{discountRate}%
              </Box>
              <Box
                component="img"
                src="/icons/discount-tags.png"
                alt=""
                sx={{
                  position: "absolute",
                  top: "-8px",
                  insetInlineEnd: "-8px",
                  width: "22px",
                  height: "22px",
                  transform: isHebrew ? "scaleX(-1)" : "none",
                }}
              />
            </Box>
          )}

          <Box display="flex" alignItems="baseline" gap="8px">
            {discountRate > 0 && (
              <CustomTypography
                props={{
                  color: "#2F2B3DB2",
                  fontSize: "14px",
                  fontWeight: 400,
                  textDecoration: "line-through",
                }}
              >
                {currencySymbol}
                {product.price}
              </CustomTypography>
            )}
            <CustomTypography
              props={{ color: "#257C6D", fontSize: "20px", fontWeight: 700 }}
            >
              {currencySymbol}
              {Number(discountedPrice).toFixed(2)}
            </CustomTypography>
          </Box>
        </Box>

        <CustomTypography
          props={{ color: "#2F2B3D", fontSize: "16px", fontWeight: 500, mb: "16px" }}
        >
          {product.name}
        </CustomTypography>

        {/* Single divider, only under the product name. */}
        <Box sx={{ borderTop: "1px solid #EAEAEA", mb: "4px" }} />

        {renderSection("description", t("description"), product.description)}
        {renderSection("terms", t("terms"), product.additional_terms)}
      </Box>
    </Box>
  );
};

export default ProductDetailPanel;
